// External library imports
import * as yup from 'yup';
import moment from 'moment';
import _ from 'lodash';
// Internal module import
import { ERRORS } from '../../utils/validationHelper';
import { generateHourlyTimestamps } from '../../utils/dateHelper';
import { momentTimeFormater } from '../../utils/timeHelper';
import { AFRR_MARKET_TYPES } from '../../constants';

const validateNumber = () => yup.number().typeError(ERRORS.number).required(ERRORS.required);

const capacityValidate = (approvalCapacity, market) => {
    if (AFRR_MARKET_TYPES.includes(market)) {
        return yup
            .number()
            .max(approvalCapacity, 'Capacity should be less than or equal to approved capacity')
            .typeError(ERRORS.number)
            .required(ERRORS.required)
            .test('multiple of 1', 'Capacity should be multiple of 1', (val) => Number.isInteger(val));
    } else {
        return yup.number().max(approvalCapacity, 'Capacity should be less than or equal to approved capacity').typeError(ERRORS.number).required(ERRORS.required);
    }
};

const validatePrice = () => {
    return yup
        .number()
        .required(ERRORS.required)
        .min(0)
        .test('price', 'Price should be greater than 0', (val, c) => {
            if (_.get(c, ['parent', 'capacity'], 0) > 0 && val <= 0) {
                return false;
            } else if (_.get(c, ['parent', 'capacity'], 0) <= 0 && val !== 0) {
                const { path, createError } = c;
                return createError({
                    path,
                    message: 'Price should be 0',
                });
            } else {
                return true;
            }
        });
};

const validateCapacityAndPrice = () =>
    yup.object().shape({
        capacity: validateNumber(),
        price: validateNumber(),
    });

export const resultMessageValidator = (isPrimaryBid = true) => {
    const wrapper = {
        reserveBidIdentification: yup.string().required(ERRORS.required),
        primaryResultMessageId: isPrimaryBid ? yup.number().required(ERRORS.required) : yup.number().optional(),
        secondaryResultMessageId: isPrimaryBid ? yup.number().optional() : yup.number().required(ERRORS.required),
    };
    return yup.object().shape(wrapper);
};

export const createBidValidationDynamically = (date, market, approvedCapacity) => {
    date = momentTimeFormater(momentTimeFormater(date).format('YYYY-MM-DD'));

    const timestampsForWholeDay = generateHourlyTimestamps(date, moment(date).add(1, 'day'));
    const hourlyDataValidation = {};
    timestampsForWholeDay.forEach(
        (hour) =>
            (hourlyDataValidation[hour] = yup.object().shape({
                capacity: capacityValidate(approvedCapacity, market),
                price: validatePrice(),
                ...(AFRR_MARKET_TYPES.includes(market)
                    ? {
                          regulationPrice: validatePrice(),
                      }
                    : {}),
            }))
    );
    return yup.object().shape({
        hourlyData: yup.object().shape(hourlyDataValidation),
        reserveBidIdentification: ['FCR-D-INC', 'FCR-D-DEC', 'FFR'].includes(market) ? yup.string().required(ERRORS.required) : yup.string(),
        isExternal: yup.boolean().required(),
    });
};

export const createFcrBidValidation = yup.object().shape({
    slot1: validateCapacityAndPrice(),
    slot2: validateCapacityAndPrice(),
    slot3: validateCapacityAndPrice(),
    slot4: validateCapacityAndPrice(),
    slot5: validateCapacityAndPrice(),
    slot6: validateCapacityAndPrice(),
});

export const CreateSecondaryBidValidation = (bidStatus, approvedCapacity) => {
    return yup.object().shape({
        capacity: yup.array().of(
            yup.mixed().test({
                name: 'isNotNull',
                message: ERRORS.required,
                test: (value, c) => {
                    const combinedCap = _.get(bidStatus, [c.options.index, 'capacity'], 0) + value;
                    if (value === null || (value && combinedCap <= approvedCapacity) || value == 0) {
                        return true;
                    } else if (combinedCap > approvedCapacity) {
                        return c.createError({
                            message: `Capacity should be less than or equal to approved capacity.`,
                            path: c.path,
                        });
                    } else {
                        return false;
                    }
                },
            })
        ),
        price: yup.array().of(
            yup.mixed().test({
                name: 'isNotNull',
                message: ERRORS.required,
                test: (value, c) => {
                    const capacity = _.get(c, ['from', 0, 'value', 'capacity'], []);
                    const idx = _.get(c, ['options', 'index']);
                    if (_.get(capacity, [idx], 0) > 0 && value > 0) {
                        return true;
                    } else {
                        const { path, createError } = c;
                        if (_.get(capacity, [idx], 0) > 0 && value <= 0) {
                            return createError({
                                path,
                                message: 'Price should be greater than 0',
                            });
                        } else if (_.get(capacity, [idx], 0) <= 0 && value !== 0) {
                            return createError({
                                path,
                                message: 'Price should be 0',
                            });
                        } else if (value === null || value >= 0) {
                            return true;
                        } else {
                            return false;
                        }
                    }
                },
            })
        ),
    });
};

export const notesValidation = () => {
    const wrapper = {
        description: yup.string().required(ERRORS.required),
    };
    return yup.object().shape(wrapper);
};

export const updateRegulationPriceAvailability = (selectedHours = [], status) => {
    const regulationPriceShape = {};

    selectedHours.map((h) => {
        _.set(regulationPriceShape, h, yup.number().min(0).required(ERRORS.required));
    });

    return yup.object().shape({
        regulationPrice: status === 'active' ? yup.object().shape(regulationPriceShape) : yup.object().shape({}),
        status: yup.string().required(ERRORS.required),
    });
};
